import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransporterNetworkTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicleType, setSelectedVehicleType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');

  // Mock data for transporters
  const transporters = [
    {
      id: 1,
      name: 'SafeLoad Logistics Pvt Ltd',
      location: 'Pune, Maharashtra',
      rating: 4.8,
      totalVehicles: 45,
      contact: {
        phone: '+91 98765 43210',
        email: 'contact@safeload.com'
      },
      vehicles: [
        {
          type: '32-Foot Truck',
          count: 15,
          capacity: '25 MT',
          ratePerTon: 850,
          distanceRange: '0-300 km',
          features: ['GPS Tracking', 'Temperature Control', 'Insurance Covered']
        },
        {
          type: '20-Foot Container',
          count: 20,
          capacity: '15 MT',
          ratePerTon: 950,
          distanceRange: '0-500 km',
          features: ['GPS Tracking', 'Sealed Container', 'Insurance Covered']
        },
        {
          type: 'Open Body Truck',
          count: 10,
          capacity: '20 MT',
          ratePerTon: 750,
          distanceRange: '0-200 km',
          features: ['GPS Tracking', 'Tarpaulin Cover']
        }
      ],
      certifications: ['ISO 9001', 'GST Registered', 'PAN Verified'],
      yearsInBusiness: 12,
      completedTrips: 5430,
      onTimeDelivery: 98
    },
    {
      id: 2,
      name: 'Express Transport Solutions',
      location: 'Mumbai, Maharashtra',
      rating: 4.6,
      totalVehicles: 32,
      contact: {
        phone: '+91 98234 56789',
        email: 'info@expresstrans.com'
      },
      vehicles: [
        {
          type: '32-Foot Truck',
          count: 12,
          capacity: '25 MT',
          ratePerTon: 900,
          distanceRange: '0-400 km',
          features: ['GPS Tracking', 'Real-time Updates', 'Insurance Covered']
        },
        {
          type: '24-Foot Truck',
          count: 20,
          capacity: '18 MT',
          ratePerTon: 1000,
          distanceRange: '0-350 km',
          features: ['GPS Tracking', 'Temperature Monitoring']
        }
      ],
      certifications: ['ISO 9001', 'GST Registered', 'FSSAI Approved'],
      yearsInBusiness: 8,
      completedTrips: 3250,
      onTimeDelivery: 96
    },
    {
      id: 3,
      name: 'Reliable Cargo Movers',
      location: 'Kolhapur, Maharashtra',
      rating: 4.7,
      totalVehicles: 28,
      contact: {
        phone: '+91 97654 32109',
        email: 'support@reliablecargo.com'
      },
      vehicles: [
        {
          type: '32-Foot Truck',
          count: 10,
          capacity: '25 MT',
          ratePerTon: 800,
          distanceRange: '0-250 km',
          features: ['GPS Tracking', 'Insurance Covered', '24/7 Support']
        },
        {
          type: 'Mini Truck',
          count: 18,
          capacity: '5 MT',
          ratePerTon: 1200,
          distanceRange: '0-150 km',
          features: ['GPS Tracking', 'Quick Delivery']
        }
      ],
      certifications: ['GST Registered', 'PAN Verified'],
      yearsInBusiness: 15,
      completedTrips: 7680,
      onTimeDelivery: 97
    },
    {
      id: 4,
      name: 'Swift Freight Services',
      location: 'Sangli, Maharashtra',
      rating: 4.9,
      totalVehicles: 55,
      contact: {
        phone: '+91 99876 54321',
        email: 'operations@swiftfreight.com'
      },
      vehicles: [
        {
          type: '32-Foot Truck',
          count: 25,
          capacity: '25 MT',
          ratePerTon: 820,
          distanceRange: '0-400 km',
          features: ['GPS Tracking', 'Temperature Control', 'Insurance Covered', 'Live Camera']
        },
        {
          type: '20-Foot Container',
          count: 15,
          capacity: '15 MT',
          ratePerTon: 920,
          distanceRange: '0-500 km',
          features: ['GPS Tracking', 'Sealed Container', 'Insurance Covered']
        },
        {
          type: '24-Foot Truck',
          count: 15,
          capacity: '18 MT',
          ratePerTon: 880,
          distanceRange: '0-350 km',
          features: ['GPS Tracking', 'Real-time Updates']
        }
      ],
      certifications: ['ISO 9001', 'ISO 14001', 'GST Registered', 'FSSAI Approved'],
      yearsInBusiness: 18,
      completedTrips: 9540,
      onTimeDelivery: 99
    }
  ];

  const vehicleTypes = ['all', ...new Set(transporters.flatMap(t => t.vehicles.map(v => v.type)))];
  const locations = ['all', ...new Set(transporters.map(t => t.location))];

  const filteredTransporters = transporters?.filter(transporter => {
    const matchesSearch = transporter?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         transporter?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesLocation = selectedLocation === 'all' || transporter?.location === selectedLocation;
    const matchesVehicleType = selectedVehicleType === 'all' || 
                               transporter?.vehicles?.some(v => v?.type === selectedVehicleType);
    return matchesSearch && matchesLocation && matchesVehicleType;
  });

  const totalVehicles = filteredTransporters?.reduce((sum, t) => sum + t?.totalVehicles, 0);
  const avgRating = filteredTransporters?.length > 0 
    ? (filteredTransporters?.reduce((sum, t) => sum + t?.rating, 0) / filteredTransporters?.length)?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="Building2" size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Transporters</p>
              <p className="text-2xl font-bold text-foreground">{filteredTransporters?.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Icon name="Truck" size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Vehicles</p>
              <p className="text-2xl font-bold text-foreground">{totalVehicles}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Icon name="Star" size={20} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Rating</p>
              <p className="text-2xl font-bold text-foreground">{avgRating}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Icon name="Package" size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Vehicle Types</p>
              <p className="text-2xl font-bold text-foreground">{vehicleTypes?.length - 1}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Search</label>
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search transporter..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] text-foreground"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Vehicle Type</label>
            <select
              value={selectedVehicleType}
              onChange={(e) => setSelectedVehicleType(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] text-foreground"
            >
              {vehicleTypes?.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Vehicle Types' : type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] text-foreground"
            >
              {locations?.map(location => (
                <option key={location} value={location}>
                  {location === 'all' ? 'All Locations' : location}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Transporter Cards */}
      <div className="space-y-6">
        {filteredTransporters?.map((transporter) => (
          <div key={transporter?.id} className="bg-card rounded-lg border border-border p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Transporter Info */}
              <div className="lg:w-1/3 border-r border-border pr-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">{transporter?.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Icon name="MapPin" size={16} />
                      <span>{transporter?.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} className="text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold text-foreground">{transporter?.rating}</span>
                      <span className="text-xs text-muted-foreground ml-1">
                        ({transporter?.completedTrips} trips)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Experience</span>
                    <span className="font-medium text-foreground">{transporter?.yearsInBusiness} years</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Fleet Size</span>
                    <span className="font-medium text-foreground">{transporter?.totalVehicles} vehicles</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">On-Time Delivery</span>
                    <span className="font-medium text-green-600">{transporter?.onTimeDelivery}%</span>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-2">Certifications</p>
                  <div className="flex flex-wrap gap-2">
                    {transporter?.certifications?.map((cert, idx) => (
                      <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="Phone" size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{transporter?.contact?.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <span className="text-foreground break-all">{transporter?.contact?.email}</span>
                  </div>
                </div>
              </div>

              {/* Vehicle Fleet */}
              <div className="lg:w-2/3">
                <h4 className="text-md font-semibold text-foreground mb-4">Available Vehicles</h4>
                <div className="space-y-4">
                  {transporter?.vehicles?.map((vehicle, idx) => (
                    <div key={idx} className="bg-background rounded-lg p-4 border border-border">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Icon name="Truck" size={20} className="text-blue-600" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-foreground">{vehicle?.type}</h5>
                              <p className="text-xs text-muted-foreground">
                                {vehicle?.count} vehicles available
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div>
                              <p className="text-xs text-muted-foreground">Capacity</p>
                              <p className="font-medium text-foreground">{vehicle?.capacity}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Rate/MT</p>
                              <p className="font-medium text-green-600">₹{vehicle?.ratePerTon}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Distance</p>
                              <p className="font-medium text-foreground">{vehicle?.distanceRange}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Available</p>
                              <p className="font-medium text-foreground">{vehicle?.count} units</p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {vehicle?.features?.map((feature, fidx) => (
                              <span key={fidx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="md:w-32">
                          <Button
                            size="sm"
                            className="w-full bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
                            onClick={() => {}}
                          >
                            <Icon name="Plus" size={16} />
                            <span>Book</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredTransporters?.length === 0 && (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <Icon name="Search" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Transporters Found</h3>
          <p className="text-muted-foreground">Try adjusting your filters to see more results</p>
        </div>
      )}
    </div>
  );
};

export default TransporterNetworkTab;